/**
 * Elevator - Moving platform that travels vertically between floors
 * Players can ride the elevator to access different levels/rooms
 */

import * as THREE from 'three';
import { Entity } from './Entity';
import type { Vector2 } from '../../types/game';
import { getPhysicsEngine } from '../../engine/PhysicsEngine';
import { getCollisionSystem } from '../../engine/CollisionSystem';

export interface ElevatorConfig {
  id: string;
  position: Vector2; // Starting position
  size: { width: number; height: number };
  topPosition: number; // Y coordinate of top position
  bottomPosition: number; // Y coordinate of bottom position
  speed: number; // Pixels per second
  waitTime: number; // Seconds to wait at each floor
  startsAt: 'top' | 'bottom'; // Starting position (overridden by startsAtFloor)
  startsAtFloor?: number; // Floor index to start at (0 = bottom, overrides startsAt)
  floorStops?: number[]; // Explicit Y positions for each floor stop (bottom to top)
}

export class Elevator extends Entity {
  // Elevator properties
  private topY: number;
  private bottomY: number;
  private speed: number;
  private waitTime: number;
  private currentWaitTime: number = 0;

  // State
  private direction: 'up' | 'down' | 'waiting';
  private targetY: number;

  // Floor stops for player-controlled elevator
  private floorStops: number[];
  private currentFloorIndex: number;

  // Visual rendering (exposed as _mesh for Game.ts to pick up)
  public _mesh: THREE.Group | null = null;
  private platformMesh: THREE.Mesh | null = null;
  private shaftMesh: THREE.Mesh | null = null;
  private cableMesh: THREE.Mesh | null = null;
  private floorIndicators: THREE.Mesh[] = [];

  // Passengers
  private passengers: Set<string> = new Set(); // Entity IDs riding the elevator

  // Floor platform (solid bottom of elevator car - player stands on this)
  private floorPlatform: {
    id: string; type: string;
    position: { x: number; y: number };
    size: { width: number; height: number };
    velocity: { x: number; y: number };
  } | null = null;
  private readonly FLOOR_HEIGHT: number = 8;

  // Ceiling platform (solid top of elevator car - blocks player from jumping through)
  private ceilingPlatform: {
    id: string; type: string;
    position: { x: number; y: number };
    size: { width: number; height: number };
    velocity: { x: number; y: number };
  } | null = null;
  private readonly CEILING_HEIGHT: number = 8;

  // Interaction
  public interactionRadius: number = 80;
  private isPlayerInsideElevator: boolean = false;

  constructor(config: ElevatorConfig) {
    super(config.id, 'elevator', config.position, config.size);

    this.topY = config.topPosition;
    this.bottomY = config.bottomPosition;
    this.speed = config.speed;
    this.waitTime = config.waitTime;

    // Setup floor stops (player-controlled elevator)
    this.floorStops = config.floorStops || [config.bottomPosition, config.topPosition];

    // Set starting position - elevator stays idle until called
    if (config.startsAtFloor !== undefined &&
        config.startsAtFloor >= 0 &&
        config.startsAtFloor < this.floorStops.length) {
      this.currentFloorIndex = config.startsAtFloor;
      this.position.y = this.floorStops[this.currentFloorIndex];
    } else if (config.startsAt === 'top') {
      this.position.y = this.topY;
      this.currentFloorIndex = this.floorStops.length - 1;
    } else {
      this.position.y = this.bottomY;
      this.currentFloorIndex = 0;
    }
    this.targetY = this.position.y;
    this.direction = 'waiting';

    // Elevators don't have health
    this.health = -1;
    this.maxHealth = -1;

    // Create floor platform object (registered with systems on initialize())
    this.floorPlatform = {
      id: `${config.id}-floor`,
      type: 'static',
      position: {
        x: config.position.x,
        y: this.position.y - config.size.height / 2 + this.FLOOR_HEIGHT / 2,
      },
      size: { width: config.size.width - 4, height: this.FLOOR_HEIGHT },
      velocity: { x: 0, y: 0 },
    };

    // Create ceiling platform object (solid top — prevents player jumping through roof)
    this.ceilingPlatform = {
      id: `${config.id}-ceiling`,
      type: 'static',
      position: {
        x: config.position.x,
        y: this.position.y + config.size.height / 2 - this.CEILING_HEIGHT / 2,
      },
      size: { width: config.size.width - 4, height: this.CEILING_HEIGHT },
      velocity: { x: 0, y: 0 },
    };

    // Create visual mesh
    this.createMesh();
  }

  // Door meshes for visual representation
  private leftDoorMesh: THREE.Mesh | null = null;
  private rightDoorMesh: THREE.Mesh | null = null;
  private doorOpenAmount: number = 0; // 0 = closed, 1 = fully open
  private doorsOpen: boolean = false;
  private doorAnimating: boolean = false;
  private readonly DOOR_SPEED: number = 2.0; // Open/close speed (units per second)
  private doorOpenTimer: number = 0;
  private readonly DOOR_OPEN_DURATION: number = 3.0; // Seconds doors stay open

  /**
   * Create Three.js mesh for the elevator
   */
  private createMesh(): void {
    this._mesh = new THREE.Group();

    // Create elevator shaft background (darker area behind elevator)
    const shaftHeight = Math.abs(this.topY - this.bottomY) + 60;
    const shaftGeometry = new THREE.PlaneGeometry(this.size.width + 20, shaftHeight);
    const shaftMaterial = new THREE.MeshBasicMaterial({
      color: 0x1a1a1a,
      opacity: 1,
      transparent: false,
    });
    this.shaftMesh = new THREE.Mesh(shaftGeometry, shaftMaterial);
    this.shaftMesh.position.set(0, (this.topY + this.bottomY) / 2, -3);
    this._mesh.add(this.shaftMesh);

    // Create cable (vertical line) - metallic look
    const cableHeight = Math.abs(this.topY - this.bottomY) + 100;
    const cableGeometry = new THREE.PlaneGeometry(8, cableHeight);
    const cableMaterial = new THREE.MeshBasicMaterial({
      color: 0x4a4a5a,
      opacity: 0.9,
      transparent: true,
    });
    this.cableMesh = new THREE.Mesh(cableGeometry, cableMaterial);
    this.cableMesh.position.set(0, (this.topY + this.bottomY) / 2, -2);
    this._mesh.add(this.cableMesh);

    // Create platform (the elevator car) - metallic body
    const platformGeometry = new THREE.PlaneGeometry(this.size.width, this.size.height);
    const platformMaterial = new THREE.MeshBasicMaterial({
      color: 0x5a5a6a,
    });
    this.platformMesh = new THREE.Mesh(platformGeometry, platformMaterial);
    this._mesh.add(this.platformMesh);

    // Create elevator frame/borders - industrial yellow/black striping
    const frameThickness = 8;
    const frameColor = 0xd4a017;

    // Top border
    const topBorder = new THREE.Mesh(
      new THREE.PlaneGeometry(this.size.width + 16, frameThickness),
      new THREE.MeshBasicMaterial({ color: frameColor })
    );
    topBorder.position.set(0, this.size.height / 2 + 4, 1);
    this.platformMesh.add(topBorder);

    // Bottom border
    const bottomBorder = new THREE.Mesh(
      new THREE.PlaneGeometry(this.size.width + 16, frameThickness),
      new THREE.MeshBasicMaterial({ color: frameColor })
    );
    bottomBorder.position.set(0, -this.size.height / 2 - 4, 1);
    this.platformMesh.add(bottomBorder);

    // Create LEFT DOOR (slides left to open)
    const doorWidth = this.size.width / 2 + 4;
    const doorHeight = this.size.height - 10;
    const leftDoorGeo = new THREE.PlaneGeometry(doorWidth, doorHeight);
    const doorMat = new THREE.MeshBasicMaterial({ color: 0x6a6a7a });
    this.leftDoorMesh = new THREE.Mesh(leftDoorGeo, doorMat);
    this.leftDoorMesh.position.set(-doorWidth / 2, 0, 2);
    this.platformMesh.add(this.leftDoorMesh);

    // Left door handle
    const handleGeo = new THREE.PlaneGeometry(4, 20);
    const handleMat = new THREE.MeshBasicMaterial({ color: 0xc0c0c0 });
    const leftHandle = new THREE.Mesh(handleGeo, handleMat);
    leftHandle.position.set(doorWidth / 2 - 8, 0, 1);
    this.leftDoorMesh.add(leftHandle);

    // Create RIGHT DOOR (slides right to open)
    this.rightDoorMesh = new THREE.Mesh(leftDoorGeo.clone(), doorMat.clone());
    this.rightDoorMesh.position.set(doorWidth / 2, 0, 2);
    this.platformMesh.add(this.rightDoorMesh);

    // Right door handle
    const rightHandle = new THREE.Mesh(handleGeo.clone(), handleMat.clone());
    rightHandle.position.set(-doorWidth / 2 + 8, 0, 1);
    this.rightDoorMesh.add(rightHandle);

    // Floor indicator panel (above doors)
    const panelGeo = new THREE.PlaneGeometry(60, 20);
    const panelMat = new THREE.MeshBasicMaterial({ color: 0x1a1a2a });
    const panel = new THREE.Mesh(panelGeo, panelMat);
    panel.position.set(0, this.size.height / 2 - 10, 3);
    this.platformMesh.add(panel);

    // Floor indicator lights
    for (let i = 0; i < this.floorStops.length; i++) {
      const isCurrentFloor = i === this.currentFloorIndex;
      const lightGeo = new THREE.CircleGeometry(5, 8);
      const lightMat = new THREE.MeshBasicMaterial({
        color: isCurrentFloor ? 0x00ff00 : 0x333333
      });
      const light = new THREE.Mesh(lightGeo, lightMat);
      const spacing = 20;
      const startX = -(this.floorStops.length - 1) * spacing / 2;
      light.position.set(startX + i * spacing, this.size.height / 2 - 10, 4);
      this.platformMesh.add(light);
      this.floorIndicators.push(light);
    }

    // Position the mesh
    this._mesh.position.set(this.position.x, this.position.y, 0);

    // Start with doors closed
    this.doorsOpen = false;
    this.doorOpenAmount = 0;
    this.doorOpenTimer = 0;
    this.updateDoorPositions();
  }

  /**
   * Register elevator with physics engine as kinematic
   */
  protected override registerPhysics(): void {
    const physicsEngine = getPhysicsEngine();
    physicsEngine.addEntity(this as any, {
      mass: 1.0,
      gravity: 0, // No gravity
      friction: 0.8,
      restitution: 0.0,
      isKinematic: true, // Elevator is kinematic (moves but not affected by physics)
      isGrounded: false,
      canJump: false,
    });
    // Register floor platform with physics (kinematic, no gravity)
    if (this.floorPlatform) {
      physicsEngine.addEntity(this.floorPlatform as any, {
        mass: 1.0, gravity: 0, friction: 0.8, restitution: 0.0,
        isKinematic: true, isGrounded: false, canJump: false,
      });
    }
    // Register ceiling platform with physics (kinematic, no gravity)
    if (this.ceilingPlatform) {
      physicsEngine.addEntity(this.ceilingPlatform as any, {
        mass: 1.0, gravity: 0, friction: 0.8, restitution: 0.0,
        isKinematic: true, isGrounded: false, canJump: false,
      });
    }
  }

  /**
   * Register elevator with collision system
   */
  protected override registerCollision(): void {
    const collisionSystem = getCollisionSystem();
    // Body: passable - player walks through the open doors
    collisionSystem.addCollider(this as any, {
      type: 'box',
      isTrigger: true,
      layer: 'trigger',
    });
    // Floor: solid - player stands on this inside the elevator
    if (this.floorPlatform) {
      collisionSystem.addCollider(this.floorPlatform as any, {
        type: 'box',
        isTrigger: false,
        layer: 'static',
      });
    }
    // Ceiling: solid - blocks player from jumping through the roof
    if (this.ceilingPlatform) {
      collisionSystem.addCollider(this.ceilingPlatform as any, {
        type: 'box',
        isTrigger: false,
        layer: 'static',
      });
    }
  }

  /**
   * Update elevator logic
   */
  public update(deltaTime: number): void {
    if (this.destroyed || !this.active) return;

    // Update door animation
    this.updateDoors(deltaTime);

    // Update movement (only moves when doors are closed)
    this.updateMovement(deltaTime);

    // Update mesh position
    if (this._mesh && this.platformMesh) {
      this._mesh.position.set(this.position.x, this.position.y, 0);
      this.platformMesh.position.set(0, 0, 0);
    }

    // Keep shaft background and cable at a fixed world Y so they don't move
    // with the elevator car and poke through the ceiling.
    // fixedLocalY = targetWorldY - _mesh.position.y
    const fixedLocalY = (this.topY + this.bottomY) / 2 - this.position.y;
    if (this.shaftMesh) this.shaftMesh.position.y = fixedLocalY;
    if (this.cableMesh) this.cableMesh.position.y = fixedLocalY;

    // Sync floor platform with elevator position
    if (this.floorPlatform) {
      this.floorPlatform.position.x = this.position.x;
      this.floorPlatform.position.y = this.position.y - this.size.height / 2 + this.FLOOR_HEIGHT / 2;
    }

    // Sync ceiling platform with elevator position
    if (this.ceilingPlatform) {
      this.ceilingPlatform.position.x = this.position.x;
      this.ceilingPlatform.position.y = this.position.y + this.size.height / 2 - this.CEILING_HEIGHT / 2;
    }

    // Update floor indicator lights
    this.updateFloorIndicators();

    // Update passengers (move them with the elevator)
    this.updatePassengers(deltaTime);
  }

  /**
   * Update door opening/closing animation
   */
  private updateDoors(deltaTime: number): void {
    if (this.doorsOpen) {
      // Doors opening
      if (this.doorOpenAmount < 1) {
        this.doorOpenAmount = Math.min(1, this.doorOpenAmount + this.DOOR_SPEED * deltaTime);
        this.doorAnimating = true;
        this.updateDoorPositions();
      } else {
        this.doorAnimating = false;
        // Count down open timer
        this.doorOpenTimer -= deltaTime;
        if (this.doorOpenTimer <= 0 && this.direction !== 'waiting') {
          // Close doors before moving
          this.doorsOpen = false;
        }
      }
    } else {
      // Doors closing
      if (this.doorOpenAmount > 0) {
        this.doorOpenAmount = Math.max(0, this.doorOpenAmount - this.DOOR_SPEED * deltaTime);
        this.doorAnimating = true;
        this.updateDoorPositions();
      } else {
        this.doorAnimating = false;
      }
    }
  }

  /**
   * Update door mesh positions based on open amount
   */
  private updateDoorPositions(): void {
    if (!this.leftDoorMesh || !this.rightDoorMesh) return;
    const doorWidth = this.size.width / 2 + 4;
    const slideDistance = doorWidth * this.doorOpenAmount;
    this.leftDoorMesh.position.x = -doorWidth / 2 - slideDistance;
    this.rightDoorMesh.position.x = doorWidth / 2 + slideDistance;
  }

  /**
   * Update floor indicator lights
   */
  private updateFloorIndicators(): void {
    this.floorIndicators.forEach((light, i) => {
      const isCurrentFloor = i === this.currentFloorIndex;
      (light.material as THREE.MeshBasicMaterial).color.setHex(
        isCurrentFloor ? 0x00ff00 : 0x333333
      );
    });
  }

  /**
   * Update elevator movement - PLAYER CONTROLLED
   * Elevator stays idle until callToFloor() is called
   * Only moves when doors are fully closed
   */
  private updateMovement(deltaTime: number): void {
    if (this.direction === 'waiting') {
      this.velocity.y = 0;
      return;
    }

    // Don't move while doors are open or animating
    if (this.doorOpenAmount > 0) {
      this.velocity.y = 0;
      return;
    }

    if (this.direction === 'up') {
      const newY = this.position.y + this.speed * deltaTime;
      if (newY >= this.targetY) {
        this.position.y = this.targetY;
        this.direction = 'waiting';
        this.velocity.y = 0;
        // Open doors on arrival
        this.doorsOpen = true;
        this.doorOpenTimer = this.DOOR_OPEN_DURATION;
        console.log(`🛗 ${this.id} arrived at floor ${this.currentFloorIndex + 1} - doors opening`);
      } else {
        this.position.y = newY;
        this.velocity.y = this.speed;
      }
    } else if (this.direction === 'down') {
      const newY = this.position.y - this.speed * deltaTime;
      if (newY <= this.targetY) {
        this.position.y = this.targetY;
        this.direction = 'waiting';
        this.velocity.y = 0;
        // Open doors on arrival
        this.doorsOpen = true;
        this.doorOpenTimer = this.DOOR_OPEN_DURATION;
        console.log(`🛗 ${this.id} arrived at floor ${this.currentFloorIndex + 1} - doors opening`);
      } else {
        this.position.y = newY;
        this.velocity.y = -this.speed;
      }
    }
  }

  /**
   * Update passengers riding the elevator
   */
  private updatePassengers(deltaTime: number): void {
    // Passenger movement handled by collision system
    // Players/entities automatically move with platforms they're standing on
  }

  /**
   * Render elevator
   */
  public render(): void {
    // Mesh is automatically rendered by Three.js scene
    // This method exists for compatibility with Entity interface
  }

  /**
   * Add passenger to elevator
   */
  public addPassenger(entityId: string): void {
    this.passengers.add(entityId);
  }

  /**
   * Remove passenger from elevator
   */
  public removePassenger(entityId: string): void {
    this.passengers.delete(entityId);
  }

  /**
   * Check if entity is on the elevator
   */
  public hasPassenger(entityId: string): boolean {
    return this.passengers.has(entityId);
  }

  /**
   * Call elevator to a specific floor index
   * Closes doors first, then moves to target floor
   */
  public callToFloor(floorIndex: number): void {
    if (floorIndex < 0 || floorIndex >= this.floorStops.length) return;
    if (this.direction !== 'waiting') return; // Already moving
    if (this.doorAnimating) return; // Doors are animating

    const targetFloorY = this.floorStops[floorIndex];

    // Don't move if already at target floor
    if (Math.abs(targetFloorY - this.position.y) < 2) return;

    this.currentFloorIndex = floorIndex;
    this.targetY = targetFloorY;

    // Close doors first - movement will start when doors finish closing
    this.doorsOpen = false;

    if (targetFloorY > this.position.y + 2) {
      this.direction = 'up';
      console.log(`🛗 ${this.id} closing doors, going UP to floor ${floorIndex + 1}`);
    } else if (targetFloorY < this.position.y - 2) {
      this.direction = 'down';
      console.log(`🛗 ${this.id} closing doors, going DOWN to floor ${floorIndex + 1}`);
    }
  }

  /**
   * Send elevator up one floor
   * Returns: 'moved' | 'at_top' | 'busy'
   */
  public goUp(): 'moved' | 'at_top' | 'busy' {
    if (this.direction !== 'waiting') return 'busy';
    const nextFloor = this.currentFloorIndex + 1;
    if (nextFloor >= this.floorStops.length) {
      return 'at_top';
    }
    this.callToFloor(nextFloor);
    return 'moved';
  }

  /**
   * Send elevator down one floor
   * Returns: 'moved' | 'at_bottom' | 'busy'
   */
  public goDown(): 'moved' | 'at_bottom' | 'busy' {
    if (this.direction !== 'waiting') return 'busy';
    const prevFloor = this.currentFloorIndex - 1;
    if (prevFloor < 0) {
      return 'at_bottom';
    }
    this.callToFloor(prevFloor);
    return 'moved';
  }

  /**
   * Cycle to next floor (F1→F2→F3→F1)
   */
  public goToNextFloor(): void {
    if (this.direction !== 'waiting') return;
    const nextFloor = (this.currentFloorIndex + 1) % this.floorStops.length;
    this.callToFloor(nextFloor);
  }

  /**
   * Get current floor index (0-based)
   */
  public getCurrentFloorIndex(): number {
    return this.currentFloorIndex;
  }

  /**
   * Get number of floor stops
   */
  public getFloorCount(): number {
    return this.floorStops.length;
  }

  /**
   * Check if elevator is idle (waiting at a floor)
   */
  public isIdle(): boolean {
    return this.direction === 'waiting';
  }

  /** Position to teleport the player to when entering the elevator */
  public getInsidePosition(): { x: number; y: number } {
    const floorTopY = this.position.y - this.size.height / 2 + this.FLOOR_HEIGHT;
    return { x: this.position.x, y: floorTopY + 24 }; // 24 = player half-height
  }

  /** Position to teleport the player to when exiting the elevator */
  public getOutsidePosition(): { x: number; y: number } {
    return { x: this.position.x + this.size.width, y: this.position.y - 40 };
  }

  public setPlayerInsideElevator(inside: boolean): void {
    this.isPlayerInsideElevator = inside;
  }

  public getIsPlayerInsideElevator(): boolean {
    return this.isPlayerInsideElevator;
  }

  // Getters
  public getDirection(): 'up' | 'down' | 'waiting' {
    return this.direction;
  }

  public getTopPosition(): number {
    return this.topY;
  }

  public getBottomPosition(): number {
    return this.bottomY;
  }

  /** Y position for a given floor index */
  public getFloorY(floorIndex: number): number {
    return this.floorStops[floorIndex] ?? this.bottomY;
  }

  /** True when elevator is idle at the given floor */
  public isIdleAtFloor(floorIndex: number): boolean {
    return this.direction === 'waiting' &&
           Math.abs(this.position.y - this.getFloorY(floorIndex)) < 5;
  }

  public getSpeed(): number {
    return this.speed;
  }

  public getMesh(): THREE.Group | null {
    return this._mesh;
  }

  /**
   * Clean up resources
   */
  public override destroy(): void {
    if (this._mesh) {
      if (this.platformMesh) {
        this.platformMesh.geometry.dispose();
        (this.platformMesh.material as THREE.Material).dispose();
      }
      if (this.cableMesh) {
        this.cableMesh.geometry.dispose();
        (this.cableMesh.material as THREE.Material).dispose();
      }
      // Parent will remove from scene
    }
    if (this.floorPlatform) {
      const collisionSystem = getCollisionSystem();
      const physicsEngine = getPhysicsEngine();
      collisionSystem.removeCollider(this.floorPlatform.id);
      physicsEngine.removeEntity(this.floorPlatform.id);
      this.floorPlatform = null;
    }
    if (this.ceilingPlatform) {
      const collisionSystem = getCollisionSystem();
      const physicsEngine = getPhysicsEngine();
      collisionSystem.removeCollider(this.ceilingPlatform.id);
      physicsEngine.removeEntity(this.ceilingPlatform.id);
      this.ceilingPlatform = null;
    }
    this.passengers.clear();
    super.destroy();
  }
}
